import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, FileArchive, Loader2, Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';

import { useImportJob, useStartImport } from '../queries';
import type { ImportDestination, ImportJobStats } from '../types';

export function ConfluenceImportDialog({
  open,
  onOpenChange,
  destination,
  onImported,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  destination: ImportDestination;
  /** Called once when the import finishes — parent invalidates its lists. */
  onImported?: () => void;
}) {
  const defaultParentTitle = destination.kind === 'project' ? 'Imported from Confluence' : '';
  const [file, setFile] = useState<File | null>(null);
  const [collectionName, setCollectionName] = useState('');
  const [parentTitle, setParentTitle] = useState(defaultParentTitle);
  const [skipHome, setSkipHome] = useState(true);
  const [jobId, setJobId] = useState<string | null>(null);
  const notified = useRef(false);

  const start = useStartImport(destination);
  const jobQuery = useImportJob(jobId);
  const job = jobQuery.data ?? null;
  const status = job?.status;

  // Fire onImported once the run finishes so the parent refreshes its lists.
  useEffect(() => {
    if (status === 'done' && !notified.current) {
      notified.current = true;
      onImported?.();
    }
  }, [status, onImported]);

  function reset() {
    setFile(null);
    setCollectionName('');
    setParentTitle(defaultParentTitle);
    setSkipHome(true);
    setJobId(null);
    notified.current = false;
    start.reset();
  }

  function close(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function submit() {
    if (!file) return;
    start.mutate(
      {
        file,
        collectionName: collectionName.trim() || undefined,
        parentTitle: parentTitle.trim() || undefined,
        skipHomePage: skipHome,
      },
      { onSuccess: (created) => setJobId(created.public_id) },
    );
  }

  const phase = !jobId ? 'form' : status === 'done' ? 'done' : status === 'failed' ? 'failed' : 'running';

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import from Confluence</DialogTitle>
          <DialogDescription>
            {destination.kind === 'wiki'
              ? 'Upload a Confluence space export (HTML) — it becomes a new collection.'
              : 'Upload a Confluence space export (HTML) — pages are added to this project.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'form' ? (
          // min-w-0: DialogContent is a CSS grid; without this the grid item
          // won't shrink below the long filename, overflowing the modal.
          <div className="min-w-0 space-y-3">
            {destination.kind === 'wiki' ? (
              <div className="space-y-1.5">
                <Label htmlFor="import-collection-name">Collection name (optional)</Label>
                <Input
                  id="import-collection-name"
                  value={collectionName}
                  onChange={(e) => setCollectionName(e.target.value)}
                  placeholder="e.g. Engineering Handbook"
                />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label htmlFor="import-parent-title">Top-level page name</Label>
                <Input
                  id="import-parent-title"
                  value={parentTitle}
                  onChange={(e) => setParentTitle(e.target.value)}
                  placeholder="e.g. Imported from Confluence"
                />
                <p className="text-muted-foreground text-xs">
                  Leave blank to add the pages directly at the project root.
                </p>
              </div>
            )}

            <div className="flex items-center justify-between gap-3 rounded-md border p-3">
              <div className="min-w-0 flex-1 space-y-0.5">
                <Label htmlFor="import-skip-home">Skip Confluence home page</Label>
                <p className="text-muted-foreground text-xs">
                  Promote the space&apos;s pages to the top level instead of nesting them under
                  the home page.
                </p>
              </div>
              <Switch id="import-skip-home" checked={skipHome} onCheckedChange={setSkipHome} />
            </div>

            <div className="space-y-1.5">
              <Label>Export file</Label>
              <label
                className="border-input hover:bg-muted/50 flex cursor-pointer items-center gap-3 rounded-md border border-dashed p-4 text-sm"
                htmlFor="import-file"
              >
                <FileArchive className="text-muted-foreground size-6 shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  {file ? file.name : 'Choose a .zip file…'}
                </span>
                <input
                  id="import-file"
                  type="file"
                  accept=".zip"
                  className="sr-only"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <p className="text-muted-foreground text-xs">
                In Confluence: Space settings → Export → <strong>HTML</strong>.
              </p>
            </div>
          </div>
        ) : null}

        {phase === 'running' ? (
          <div className="flex items-center gap-3 py-6 text-sm">
            <Loader2 className="text-primary size-5 animate-spin" />
            <span>Importing your space… this can take a minute for large spaces.</span>
          </div>
        ) : null}

        {phase === 'done' && job ? <ImportSummary stats={job.stats} /> : null}

        {phase === 'failed' && job ? (
          <div className="bg-destructive/10 text-destructive flex items-start gap-2 rounded-md p-3 text-sm">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span className="min-w-0 break-words">
              {job.error || 'The import failed. Please check the file and try again.'}
            </span>
          </div>
        ) : null}

        <DialogFooter>
          {phase === 'form' ? (
            <>
              <Button variant="outline" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button onClick={submit} disabled={!file || start.isPending}>
                {start.isPending ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Upload className="size-4" />
                )}
                Import
              </Button>
            </>
          ) : phase === 'running' ? (
            <Button variant="outline" onClick={() => close(false)}>
              Close (import keeps running)
            </Button>
          ) : phase === 'failed' ? (
            <>
              <Button variant="outline" onClick={() => close(false)}>
                Close
              </Button>
              <Button onClick={reset}>Try again</Button>
            </>
          ) : (
            <Button onClick={() => close(false)}>Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportSummary({ stats }: { stats: ImportJobStats }) {
  const warnings = stats.warnings ?? [];
  return (
    <div className="min-w-0 space-y-3">
      <div className="text-foreground flex items-center gap-2 text-sm font-medium">
        <CheckCircle2 className="size-5 text-emerald-600" />
        Import complete
      </div>
      <ul className="text-muted-foreground space-y-1 text-sm">
        <li>{stats.pages_created ?? 0} pages imported</li>
        {stats.images_inlined ? <li>{stats.images_inlined} images embedded</li> : null}
        {stats.links_rewritten ? <li>{stats.links_rewritten} internal links re-linked</li> : null}
        {stats.pages_skipped ? <li>{stats.pages_skipped} pages skipped (missing in export)</li> : null}
      </ul>
      {warnings.length ? (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
          <div className="mb-1 flex items-center gap-1.5 font-medium">
            <AlertTriangle className="size-3.5" /> Some content was adjusted
          </div>
          <ul className="list-disc space-y-0.5 pl-4">
            {warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
